import avatarFB from '@/assets/images/avatar-fb.png';
import avatarIG from '@/assets/images/avatar-ig.png';
import avatarLinkedIn from '@/assets/images/avatar-linkedin.png';
import avatarSMS from '@/assets/images/sms-icon.png';
import { Loader2Icon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import TooltipComponent from '@/components/common/tooltip/tooltip';

import CopilotPredictionButton from '../copilot/CopilotPredictionButton';
import ActivityLogMessage from './Message/ActivityLogMessage';
import MessageComponent from './Message/MessageComponent';
import ReplyButton from './Message/ReplyButton';

export default function RegularMessageList({
  messages,
  fullname,
  profile_picture_url,
  defaultPic,
  channel,
  getOwnerDetails,
  hoveredMessageId,
  setHoveredMessageId,
  owners,
  currentUser,
  fbFetchLoading,
  fetchFromIG,
  load_fb_past_messages,
  page,
}) {
  // Helper to check if message can be replied to
  const canReplyToMessage = (message) => {
    return (
      channel === 'instagram' &&
      (message.is_deleted === false ||
        message.is_deleted === 'false' ||
        message.is_deleted === undefined ||
        message.is_deleted === null) &&
      message.message_type !== 'comment' &&
      message.message_type !== 'activity_log'
    );
  };

  return (
    <>
      {channel === 'instagram' ? (
        page === null ? (
          load_fb_past_messages === true ? (
            <div className='my-3 flex items-center justify-center'>
              {fbFetchLoading ? (
                <Loader2Icon className='h-4 w-4 animate-spin' />
              ) : (
                (() => {
                  const isFetchDisabled =
                    currentUser?.customer?.is_fetch_ig_disabled === true;
                  const isAdmin = currentUser?.role === 'admin';

                  return (
                    (!isFetchDisabled || isAdmin) && (
                      <Button size='xs' onClick={fetchFromIG}>
                        Fetch from IG
                      </Button>
                    )
                  );
                })()
              )}
            </div>
          ) : (
            <div className='relative mx-10 my-5 flex items-center justify-center'>
              <div className='absolute  left-0 right-0 -z-10 w-full border-t bg-primary' />
              <span className='rounded-md bg-border px-4 py-2 text-xs'>
                This is the start of conversation
              </span>
            </div>
          )
        ) : null
      ) : null}
      {messages.map((message, index) => {
        // Handle activity log messages (e.g., contact_owner_changed)
        if (message.message_type === 'activity_log') {
          return <ActivityLogMessage key={message._id} message={message} />;
        }

        const sameUserAsPrevious =
          index > 0 && messages[index - 1].user_id === message.user_id;

        const lastSentMessage = messages
          .slice()
          .reverse()
          .find((msg) => msg.direction === 'sent');

        const isLastSentMessage =
          lastSentMessage && lastSentMessage._id === message._id;

        const isNewDay =
          index > 0 &&
          new Date(messages[index - 1].timestamp).toLocaleDateString() !==
            new Date(message.timestamp).toLocaleDateString();

        const isWithinLast5Days =
          new Date() - new Date(message.timestamp) < 5 * 24 * 60 * 60 * 1000;

        const timestampMessage =
          isNewDay && index > 0 ? (
            <div
              key={`timestamp_${message.timestamp}`}
              className='my-4 text-center text-xs text-muted-foreground'
            >
              {isWithinLast5Days
                ? `${new Date(message.timestamp).toLocaleDateString('en-US', {
                    weekday: 'short',
                  })} ${new Date(message.timestamp).toLocaleTimeString(
                    'en-US',
                    {
                      hour: '2-digit',
                      minute: '2-digit',
                    }
                  )}`
                : `${new Date(message.timestamp).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'short',
                  })} at ${new Date(message.timestamp).toLocaleTimeString(
                    'en-US',
                    {
                      hour: '2-digit',
                      minute: '2-digit',
                    }
                  )}`}
            </div>
          ) : null;

        return (
          <div
            className='group/copilot relative whitespace-pre-wrap break-words'
            style={{ overflowWrap: 'anywhere' }}
            key={message._id}
            onMouseEnter={() => setHoveredMessageId(message._id)}
            onMouseLeave={() => setHoveredMessageId(null)}
          >
            {timestampMessage}
            <div
              className={cn(
                'mx-4 flex items-end space-x-2',
                message.direction === 'sent' ? 'ml-auto' : 'mr-auto',
                sameUserAsPrevious
                  ? message.emoji
                    ? 'mb-5 mt-3'
                    : 'my-3'
                  : 'my-3',
                index === messages.length - 1 ? 'mb-6' : ''
              )}
            >
              {message.direction !== 'sent' && (
                <TooltipComponent
                  content={<span>{fullname}</span>}
                  delayDuration='100'
                >
                  <div>
                    <Avatar>
                      {profile_picture_url ? (
                        <AvatarImage src={profile_picture_url} />
                      ) : channel === 'sms' ? (
                        fullname?.[0] ? (
                          <AvatarFallback>
                            {fullname?.charAt(0)?.toUpperCase() || ''}
                          </AvatarFallback>
                        ) : (
                          <AvatarImage src={defaultPic} />
                        )
                      ) : (
                        <AvatarImage src={defaultPic} />
                      )}
                      {channel !== 'sms' && (
                        <AvatarFallback>
                          {fullname?.charAt(0)?.toUpperCase() || ''}
                        </AvatarFallback>
                      )}
                    </Avatar>
                  </div>
                </TooltipComponent>
              )}
              <TooltipComponent
                content={
                  <span>
                    {new Date(message.timestamp).toLocaleString('en-US')}
                  </span>
                }
                delayDuration='100'
              >
                <div
                  className={cn(
                    'group relative flex max-w-[75%] flex-col gap-2 self-center whitespace-pre-wrap',
                    message.direction === 'sent' ? 'ml-auto' : ''
                  )}
                >
                  {/* Reply button for sent Instagram messages - absolutely positioned to the left */}
                  {hoveredMessageId === message._id &&
                    message.direction === 'sent' &&
                    canReplyToMessage(message) && (
                      <ReplyButton message={message} position='left' />
                    )}
                  <MessageComponent
                    message={message}
                    isLastSentMessage={isLastSentMessage}
                  />
                </div>
              </TooltipComponent>
              {/* Reply button for received Instagram messages - appears to the right */}
              {hoveredMessageId === message._id &&
                message.direction !== 'sent' &&
                canReplyToMessage(message) && (
                  <ReplyButton message={message} position='right' />
                )}

              {hoveredMessageId === message._id &&
                message.message_type === 'text' &&
                message.direction !== 'sent' &&
                (message.is_deleted === false ||
                  message.is_deleted === 'false' ||
                  message.is_deleted === undefined ||
                  message.is_deleted === null) && (
                  <CopilotPredictionButton message={message} />
                )}

              {message.direction === 'sent' && (
                <TooltipComponent
                  content={
                    <span>
                      {getOwnerDetails(
                        message.user_id,
                        'fullname',
                        message.channel
                      )}
                    </span>
                  }
                  delayDuration='100'
                >
                  <div>
                    <Avatar>
                      <AvatarImage
                        src={getOwnerDetails(
                          message.user_id,
                          'image',
                          message.channel
                        )}
                      />
                      <AvatarFallback>
                        {getOwnerDetails(
                          message.user_id,
                          'initials',
                          message.channel
                        )}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </TooltipComponent>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
